import React, { useState } from 'react';
import { Button } from '@appkit4/react-components/button';
import { ListingTable } from '../../../shared/listingTable';
import './UploadedDocuments.scss';
import Avatar from '../../../shared/avatar/Avatar';
import UploadModal from '../../../shared/uploadModal/UploadModal';
import SightlineModal from '../sightlineModal/SightlineModal';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDate } from '../../../../helpers/helperFunctions';
import { Input, Loading } from '@appkit4/react-components';
import { DeleteModal } from '../../../shared/deleteModal';
import Illustration6 from '../../../../assets/illustrations/Illustration_6.png';
import {
    usePostV1UploadDeleteUploadedDocument,
    useGetV1SightlineProcessIdFilters,
    usePostV1SightlineProcessIdFilters,
} from '../../../../api/generated/endpoint';
import useToast from '../../../../helpers/useToast';
import useLocale from '../../../../locale/useLocale';

interface UploadedDocumentsTableProps {
    filteredData: any[];
    currentPage: number;
    pageSize: number;
    setCurrentPage: (page: number) => void;
    totalCount: number;
    isLoading?: boolean;
    activeTab?: number;
    onTabChange?: (index: number) => void;
    processName?: string;
    onUploadComplete?: () => void;
}

const UploadedDocumentsTable = ({
    filteredData,
    currentPage,
    pageSize,
    setCurrentPage,
    totalCount,
    isLoading = false,
    activeTab = 0,
    onTabChange,
    processName,
    onUploadComplete,
}: UploadedDocumentsTableProps) => {
    const { processId } = useParams<{ processId: string }>();

    const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
    const [isSightlineModalVisible, setIsSightlineModalVisible] =
        useState(false);
    const navigate = useNavigate();

    const { data: sightlineFilters, refetch: refetchSightlineFilters } =
        useGetV1SightlineProcessIdFilters(Number(processId));

    const { mutate: updateSightlineFilters } =
        usePostV1SightlineProcessIdFilters();

    const getStatusByNumber = (num: number) => {
        switch (num) {
            case 0:
                return 'Unknown';
            case 1:
                return 'Failed add to classifier service bus';
            case 2:
                return 'Failed add to excel extraction service bus';
            case 3:
                return 'Awaiting classification';
            case 4:
                return 'Classification system error';
            case 5:
                return 'Classification failed';
            case 6:
                return 'Classification low confidence';
            case 7:
                return 'Classification passed';
            case 8:
                return 'Successfully split';
            case 9:
                return 'Failed split';
            default:
                return 'Unknown';
        }
    };

    const handleEdit = (row: any, status: string) => {
        navigate(
            `/process/${processId}/uploaded/${row.uploadedDocumentId}/${status}`,
            {
                state: {
                    fileName: row.fileName,
                    processName: processName,
                    uploadedListingTab: activeTab,
                },
            }
        );
    };

    const handleUpload = () => {
        setIsUploadModalVisible(true);
    };

    const handleSightline = () => {
        refetchSightlineFilters();
        setIsSightlineModalVisible(true);
    };

    const handleUploadComplete = () => {
        if (onUploadComplete) {
            onUploadComplete();
        }
    };

    const tabs = [
        { label: 'All', value: 'all' },
        { label: 'Classification failed', value: 'failed' },
        { label: 'Classification low confidence', value: 'low-confidence' },
    ];

    const columns = [
        {
            field: 'fileName',
            header: 'File name',
        },
        {
            field: 'uploadedBy',
            header: 'Uploaded by',

            renderCell: (row: any) => (
                <div className="d-flex align-items-center gap-2">
                    <Avatar
                        name={row.uploadedBy}
                        imageUrl={row.avatarUrl}
                        size={24}
                    />
                    <span>{row.uploadedBy}</span>
                </div>
            ),
        },
        {
            field: 'uploadedDate',
            header: 'Upload date',
            renderCell: (row: any) => (
                <span>{formatDate(row.uploadedDate)}</span>
            ),
        },
        {
            field: 'uploadedDocumentStatus',
            header: 'Classification status',
            renderCell: (row: any) => {
                const getStatusIcon = (status: string) => {
                    switch (status) {
                        case 'Classification failed':
                            return (
                                <span className="Appkit4-icon icon-warning-fill status-icon error-icon" />
                            );
                        case 'Classification system error':
                        case 'Failed add to classifier service bus':
                        case 'Failed add to excel extraction service bus':
                        case 'Failed split':
                            return (
                                <span className="Appkit4-icon icon-warning-fill status-icon error-icon" />
                            );
                        case 'Classification in progress':
                        case 'Awaiting classification':
                        case 'Successfully split':
                            return (
                                <span className="status-dot success-icon">
                                    ●
                                </span>
                            );
                        case 'Classification low confidence':
                            return (
                                <span className="Appkit4-icon icon-warning-fill status-icon custom-warning-icon" />
                            );
                        case 'Classification passed':
                            return (
                                <span className="status-dot awaiting-icon">
                                    ●
                                </span>
                            );
                        default:
                            return null;
                    }
                };

                return (
                    <div className="d-flex align-items-center gap-2">
                        {getStatusIcon(
                            getStatusByNumber(row.uploadedDocumentStatus)
                        )}
                        <span>
                            {getStatusByNumber(row.uploadedDocumentStatus)}
                        </span>
                    </div>
                );
            },
        },
        {
            field: 'fileName',
            header: 'Edit',
            style: { width: '50px' },
            renderCell: (row: any) => {
                if (
                    row.uploadedDocumentStatus === 5 ||
                    row.uploadedDocumentStatus === 6
                ) {
                    return (
                        <span
                            className="Appkit4-icon icon-edit-outline action-icon"
                            role="button"
                            onClick={() => handleEdit(row, 'edit')}
                            aria-label="Edit"
                        />
                    );
                } else if (
                    row.uploadedDocumentStatus === 7 ||
                    row.uploadedDocumentStatus === 8
                ) {
                    return (
                        <span
                            className="Appkit4-icon icon-view-outline ap-font-24"
                            role="button"
                            onClick={() => handleEdit(row, 'viewonly')}
                            aria-label="View"
                        />
                    );
                } else {
                    return (
                        <span
                            className="Appkit4-icon icon-edit-outline action-icon-disabled"
                            role="button"
                            aria-label="Edit"
                        />
                    );
                }
            },
        },
        {
            field: 'fileName',
            header: 'Delete',
            style: { width: '50px' },
            renderCell: (row: any) => (
                <span
                    className="Appkit4-icon icon-delete-outline action-icon"
                    role="button"
                    onClick={() => handleDelete(row)}
                    aria-label="Delete"
                />
            ),
        },
    ];

    const handleTabChange = (index: number) => {
        if (onTabChange) {
            onTabChange(index);
        }
        navigate(`/process/${processId}/uploaded`, {
            state: { uploadedListingTab: index },
            replace: true,
        });
    };

    const handleSightlineUpdate = (data: {
        engagementIds: string[];
        categoryNames: string[];
        subCategoryNames: string[];
    }) => {
        updateSightlineFilters(
            { processId: Number(processId), data },
            {
                onSuccess: () => {
                    showToast(
                        l('_success'),
                        'Sightline filters updated',
                        'success'
                    );
                },
                onError: () => {
                    showToast(
                        l('_error'),
                        'Failed to update sightline filters',
                        'error'
                    );
                },
            }
        );
    };

    const handleDelete = (row: any) => {
        setSelectedDocumentId(row.uploadedDocumentId);
        setConfirmationVisible(true);
    };
    const [inputValue, setInputValue] = useState('');
    const [confirmationVisible, setConfirmationVisible] = useState(false);
    const [selectedDocumentId, setSelectedDocumentId] = useState<number>();
    const { mutate: deleteDocument } = usePostV1UploadDeleteUploadedDocument();
    const showToast = useToast();
    const { l } = useLocale();

    const confirmDeletion = () => {
        deleteDocument(
            { data: { uploadedDocumentId: selectedDocumentId } },
            {
                onSuccess: () => {
                    showToast(
                        l('_success'),
                        'Document has been deleted',
                        'success'
                    );
                    setConfirmationVisible(false);
                    if (onUploadComplete) {
                        onUploadComplete();
                    }
                },
                onError: () => {
                    showToast(
                        l('_error'),
                        'Failed to delete document',
                        'error'
                    );
                },
            }
        );
    };

    const deleteModalContent = () => (
        <div className="del-modal-content-wrapper">
            <img
                src={Illustration6}
                alt="Delete Confirmation"
                className="del-modal-image"
            />
            <p>Are you sure you want to delete this document?</p>
            <Input
                type="text"
                placeholder="Delete"
                className="del-folder-confirmation-input"
                value={inputValue}
                onChange={(value: string) => setInputValue(value)}
            />
            <p style={{ fontSize: 14 }}>
                Type “Delete” To Delete This Document
            </p>
            <Button
                onClick={confirmDeletion}
                kind="danger"
                disabled={inputValue.toLowerCase() !== 'delete'}
                style={{ marginTop: '40px' }}
            >
                Delete
            </Button>
        </div>
    );

    if (isLoading) {
        return (
            <div className="uploaded-documents loading-state">
                <Loading loadingType="circular" indeterminate compact={false} />
            </div>
        );
    }

    return (
        <div className="uploaded-documents">
            <ListingTable
                tabs={tabs}
                activeTab={activeTab}
                columns={columns}
                data={filteredData}
                totalCount={totalCount}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onTabChange={handleTabChange}
                headerButtons={
                    <div className="table-header-buttons">
                        <Button
                            kind="tertiary"
                            icon="icon-circle-plus-outline"
                            onClick={handleSightline}
                        >
                            Sightline Integration
                        </Button>
                        <Button
                            variant="primary"
                            icon="icon-upload-outline"
                            onClick={handleUpload}
                        >
                            Upload documents
                        </Button>
                    </div>
                }
            />

            <UploadModal
                visible={isUploadModalVisible}
                onClose={() => setIsUploadModalVisible(false)}
                onUpload={handleUploadComplete}
                processId={processId}
            />
            <SightlineModal
                visible={isSightlineModalVisible}
                onClose={() => setIsSightlineModalVisible(false)}
                onSave={handleSightlineUpdate}
                sightlineData={sightlineFilters}
                refetchData={refetchSightlineFilters}
            />
            <DeleteModal
                content={deleteModalContent()}
                visible={confirmationVisible}
                onClose={() => setConfirmationVisible(false)}
                title="Delete Document"
            />
        </div>
    );
};

export default UploadedDocumentsTable;
